import { defineComponent } from "vue";
import { OptionalVueProp, VueProp } from "../../core/vueHelpers";

export const DayClasses =
{
    dayContainer: "datePickerCalendar__dayContainer",
    dayContainerToday: "datePickerCalendar__dayContainer--today",
    dayContainerSelected: "datePickerCalendar__dayContainer--selected",
    day: "datePickerCalendar__day",
    dayToday: "datePickerCalendar__day-today",
    daySelected: "datePickerCalendar__day--selected",
    dayWeekend: "datePickerCalendar__day-weekend",
    dayOutsideMonth: "datePickerCalendar__day--outsideMonth",
    text: "datePickerCalendar__dayText",
};

export default /*#__PURE__*/ defineComponent(
    {
        name: "day",
        props:
        {
            date: VueProp<Date>(Date),
            selected: OptionalVueProp(Boolean),
            outsideMonth: OptionalVueProp(Boolean),
            handleClick: VueProp<() => void>(Function),
            tabIndex: VueProp(String),
            isToday: VueProp(Boolean)
        },
        setup(props)
        {
            const isWeekend = props.date.getDay() === 0 || props.date.getDay() === 6;

            return () => (
                <td class={{
                    [DayClasses.dayContainer]: true,
                    [DayClasses.dayContainerToday]: props.isToday,
                    [DayClasses.dayContainerSelected]: props.selected
                }}
                    role="gridcell"
                    tabindex={props.tabIndex}
                    data-date={props.date.toISOString()}
                    onKeydown={handleKeydown}
                    aria-selected={props.selected ?? false}
                    onClick={props.handleClick}>
                    <div
                        aria-label={getFormattedAriaLabel(props.date)}
                        class={{
                            [DayClasses.day]: true,
                            [DayClasses.dayToday]: props.isToday,
                            [DayClasses.dayWeekend]: isWeekend && !props.outsideMonth,
                            [DayClasses.daySelected]: props.selected,
                            [DayClasses.dayOutsideMonth]: props.outsideMonth,
                        }}>
                        <span
                            aria-hidden="true"
                            class={DayClasses.text}>{props.date.getDate()}
                        </span>
                    </div>
                </td>
            );

            function getFormattedAriaLabel(date: Date)
            {
                const day = date.getDate();
                const month = date.toLocaleDateString("en-US", { month: "long" });
                const year = date.getFullYear();
                let text = `${day}, ${month}, ${year}`
                if (props.selected)
                {
                    text += " selected";
                }
                return text;
            }

            function handleKeydown(event: KeyboardEvent)
            {
                if (event.key === "Enter")
                {
                    props.handleClick();
                    return;
                }

                if (event.key === "Tab")
                {
                    // Focus on the first focusable element after the datePickerCalendar__table element in the DOM (treat the date table as a single tabstop)
                    const table = (event.target as HTMLElement).closest(".datePickerCalendar__table") as HTMLElement;
                    if (table)
                    {
                        const focusable = "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";
                        let current: HTMLElement | null = table;
                        while (current)
                        {
                            let next = current.nextElementSibling;
                            while (next)
                            {
                                const focusableEl = next.matches(focusable) ? next : next.querySelector(focusable);
                                if (focusableEl)
                                {
                                    event.preventDefault();
                                    (focusableEl as HTMLElement).focus();
                                    return;
                                }
                                next = next.nextElementSibling;
                            }
                            current = current.parentElement;
                        }
                    }
                }
            }
        }
    }
);